import { Component } from '@angular/core';
import { CommonModule, formatDate, NgFor, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MealPlanServiceV2 } from '../../meal-plan-v2/meal-plan-v2.service';
import { SharedServiceService } from '../../services/shared-service.service';

@Component({
  selector: 'app-meal-templates',
  standalone: true,
  imports: [CommonModule, NgIf, NgFor, FormsModule],
  templateUrl: './meal-templates.component.html',
  styleUrl: './meal-templates.component.css'
})
export class MealTemplatesComponent {
  templates: any[] = [];
  templateList: any[] = [];
  userLoggedIn: any = '';

  showImportModal = false;
  members: any[] = [];
  filteredMembers: any[] = [];
  selectedMemberIds: Set<string> = new Set();
  memberSearchName = '';
  memberSearchEmail = '';
  isLoadingMembers = false;
  selectedTemplate: any = null;

  showDeleteConfirmModal = false;
  templateToDelete: any = null;
  isDeleting = false;
  showDeleteResultModal = false;
  deleteResultMessage: string | null = null;
  deleteResultError = false;

  showImportSuccessModal = false;
  importSuccessTemplateName = '';
  importSuccessMemberNames: string[] = [];
  isImportingTemplate = false;

  constructor(
    private mealPlanServiceV2: MealPlanServiceV2,
    private router: Router,
    private sharedService: SharedServiceService
  ) {}

  ngOnInit(): void {
    this.sharedService.checkLogin().subscribe({
      next: () => {
        const user = this.sharedService.getLoggedInUser();
        this.userLoggedIn = user?.id ?? '';
      }
    });
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.templates = [];
    this.templateList = [];
    this.mealPlanServiceV2.getMealPlanTemplates().subscribe((response: any) => {
      const list = response?.templates || response || [];
      this.templates = Array.isArray(list) ? list : [];
      for (let i = 0; i < this.templates.length; i += 3) {
        this.templateList.push(this.templates.slice(i, i + 3));
      }
    });
  }

  formatCardDate(d: any) {
    return formatDate(d, 'MMMM d, y', 'en-US');
  }

  formatCardTime(d: any) {
    return new Date(d).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  importForMember(template: any) {
    this.selectedTemplate = template;
    this.loadAssignedMembers();
  }

  loadAssignedMembers(): void {
    if (!this.userLoggedIn) {
      return;
    }
    this.isLoadingMembers = true;
    this.showImportModal = true;
    this.selectedMemberIds.clear();
    this.memberSearchName = '';
    this.memberSearchEmail = '';
    this.mealPlanServiceV2.getAssignedMembers(this.userLoggedIn).subscribe({
      next: (list: any[]) => {
        this.members = (list || []).map((m: any) => ({
          memberId: m.memberId,
          email: m.email ?? '',
          firstName: m.firstName ?? '',
          lastName: m.lastName ?? '',
          name: [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || '—',
          age: m.age ?? 0,
          gender: m.gender ?? '',
          timeZone: m.timeZone
        }));
        this.filteredMembers = [...this.members];
        this.isLoadingMembers = false;
      },
      error: () => {
        this.members = [];
        this.filteredMembers = [];
        this.isLoadingMembers = false;
      }
    });
  }

  closeImportModal(): void {
    this.showImportModal = false;
    this.selectedTemplate = null;
    this.selectedMemberIds.clear();
    this.memberSearchName = '';
    this.memberSearchEmail = '';
    this.filteredMembers = [...this.members];
  }

  onMemberSearchChange(): void {
    let filtered = [...this.members];
    if (this.memberSearchName?.trim()) {
      const q = this.memberSearchName.toLowerCase().trim();
      filtered = filtered.filter(m => (m.name || '').toLowerCase().includes(q));
    }
    if (this.memberSearchEmail?.trim()) {
      const q = this.memberSearchEmail.toLowerCase().trim();
      filtered = filtered.filter(m => (m.email || '').toLowerCase().includes(q));
    }
    this.filteredMembers = filtered;
  }

  toggleMemberSelection(memberId: string): void {
    if (this.selectedMemberIds.has(memberId)) {
      this.selectedMemberIds.delete(memberId);
    } else {
      this.selectedMemberIds.add(memberId);
    }
  }

  isMemberSelected(memberId: string): boolean {
    return this.selectedMemberIds.has(memberId);
  }

  onImportTemplate(): void {
    const memberIds = Array.from(this.selectedMemberIds);
    if (memberIds.length === 0 || !this.selectedTemplate || !this.userLoggedIn) return;
    const templateId = this.selectedTemplate?.TemplateId ?? this.selectedTemplate?._id ?? '';
    if (!templateId) return;

    this.isImportingTemplate = true;
    this.mealPlanServiceV2.importTemplateToMembers(templateId, this.userLoggedIn, memberIds).subscribe({
      next: (response) => {
        this.isImportingTemplate = false;
        const templateName = this.selectedTemplate?.mealPlanName ?? 'Template';
        const memberNames = this.members
          .filter((m) => this.selectedMemberIds.has(m.memberId))
          .map((m) => m.name || [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || '—');
        this.closeImportModal();
        this.importSuccessTemplateName = templateName;
        this.importSuccessMemberNames = memberNames;
        this.showImportSuccessModal = true;
      },
      error: (err) => {
        this.isImportingTemplate = false;
        console.error('Failed to import template', err);
        alert('Failed to import template. Please try again.');
      }
    });
  }

  closeImportSuccessModal(): void {
    this.showImportSuccessModal = false;
    this.importSuccessTemplateName = '';
    this.importSuccessMemberNames = [];
  }

  viewTemplate(template: any) {
    const id = template?.TemplateId ?? template?._id;
    if (!id) return;
    this.router.navigate(['/template-view'], { queryParams: { templateId: id } });
  }

  openDeleteConfirm(template: any): void {
    this.templateToDelete = template;
    this.showDeleteConfirmModal = true;
  }

  closeDeleteConfirm(): void {
    if (!this.isDeleting) {
      this.templateToDelete = null;
      this.showDeleteConfirmModal = false;
    }
  }

  confirmDeleteTemplate(): void {
    if (!this.templateToDelete) return;
    const templateId = this.templateToDelete?.TemplateId ?? this.templateToDelete?._id ?? '';
    if (!templateId) return;

    this.isDeleting = true;
    this.mealPlanServiceV2.deleteTemplate(templateId).subscribe({
      next: (response: any) => {
        this.isDeleting = false;
        this.showDeleteConfirmModal = false;
        this.templateToDelete = null;
        const message = response?.message ?? response?.msg ?? (typeof response === 'string' ? response : 'Template deleted successfully.');
        this.deleteResultMessage = message;
        this.deleteResultError = false;
        this.showDeleteResultModal = true;
        this.loadTemplates();
      },
      error: (err) => {
        this.isDeleting = false;
        const message = err?.error?.message ?? err?.error?.msg ?? err?.message ?? 'Failed to delete template. Please try again.';
        this.deleteResultMessage = message;
        this.deleteResultError = true;
        this.showDeleteResultModal = true;
        this.showDeleteConfirmModal = false;
        this.templateToDelete = null;
      }
    });
  }

  closeDeleteResultModal(): void {
    this.showDeleteResultModal = false;
    this.deleteResultMessage = null;
    this.deleteResultError = false;
  }
}

